/**
 * Profile Modal Component
 * Allows users to edit their profile
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { useSession } from '@/features/auth/hooks/useSession';
import { useWorkspacePlanTier } from '@/features/billing/hooks/useWorkspacePlanTier';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import { useIsCompactLayout } from '@/common/hooks';
import { apiFetch } from '@/lib/apiClient';
import { resolveAssistantName } from '@/lib/assistantName';
import './ProfileModal.css';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
  const { profile, token, clearSession, refresh, fetchContainers, selectedContainer } = useSession();
  const [openingPortal, setOpeningPortal] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const workspaceTier = useWorkspacePlanTier();
  const hasActiveSubscription = workspaceTier === 'pro' || workspaceTier === 'team';
  const isPaidOwner = workspaceTier !== null && hasActiveSubscription && (selectedContainer?.isOwner !== false);
  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(onClose, 30);
  const isMobile = useIsCompactLayout();
  const [name, setName] = useState(profile?.name || '');
  const [loggingOut, setLoggingOut] = useState(false);

  // Sync name with profile when it changes
  useEffect(() => {
    if (profile?.name) {
      setName(profile.name);
    }
  }, [profile?.name]);

  // Sync assistantName with profile when it changes
  useEffect(() => {
    if (profile?.containerName) {
      setAssistantName(profile.containerName);
    }
  }, [profile?.containerName]);

  const [assistantName, setAssistantName] = useState(
    resolveAssistantName(profile)
  );

  const [timezone, setTimezone] = useState(
    profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  // Sync timezone with profile when it changes
  useEffect(() => {
    if (profile?.timezone) {
      setTimezone(profile.timezone);
    }
  }, [profile?.timezone]);

  // Timezone dropdown state
  const [tzOpen, setTzOpen] = useState(false);
  const [tzSearch, setTzSearch] = useState('');
  const tzContainerRef = useRef<HTMLDivElement>(null);
  const tzDropdownRef = useRef<HTMLDivElement>(null);
  const tzButtonRef = useRef<HTMLButtonElement>(null);
  const [tzDropdownPos, setTzDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  // Close timezone dropdown on outside click
  useEffect(() => {
    if (!tzOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inContainer = tzContainerRef.current?.contains(target);
      const inDropdown = tzDropdownRef.current?.contains(target);
      if (!inContainer && !inDropdown) {
        setTzOpen(false);
        setTzSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [tzOpen]);

  // Close on escape key
  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  const saveProfile = async (updates: { user_name?: string | null; container_name?: string | null; timezone?: string | null }) => {
    try {
      // apiFetch auto-routes /auth/* to the supervisor (authBaseUrl) and carries
      // refresh-token rotation; it throws ApiError on a non-ok response.
      await apiFetch('/auth/profile', {
        method: 'PATCH',
        body: JSON.stringify(updates),
        authToken: token ?? undefined,
      });

      // Sync display name to API for activity feed (best-effort; failures swallowed).
      // apiFetch attaches X-Workspace-Id from the selected container automatically.
      if (updates.user_name !== undefined) {
        apiFetch('/users/profile', {
          method: 'PATCH',
          body: JSON.stringify({ userName: updates.user_name }),
          authToken: token ?? undefined,
        }).catch(() => {});
      }

      // Refresh the session so profile.name / .containerName propagate
      // to the sidebar (avatar + displayName) and any other consumers
      // without requiring a full page reload.
      await Promise.all([refresh(), fetchContainers()]);
    } catch (error) {
      console.error('Failed to save profile:', error);
    }
  };

  const modalContent = (
    <div className={`profile-modal-overlay ${isMobile ? 'mobile' : ''}`} onMouseDown={handleOverlayMouseDown} onClick={handleOverlayClick}>
      <div ref={modalContentRef} className={`profile-modal ${isMobile ? 'mobile' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Profile Settings</h2>
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body xv-scroll">
          <div className="profile-section">
            <div style={{ background: 'var(--color-surface-tinted, #F8F9FA)', border: '1px solid var(--color-border-soft, #ECECEF)', borderRadius: 12, padding: 16 }}>
              <div className="form-group">
                <label htmlFor="name">Display Name</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => {
                    const trimmed = name.trim();
                    if (trimmed && trimmed !== profile?.name) {
                      saveProfile({ user_name: trimmed });
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  placeholder="Enter your name"
                />
                <small className="field-hint">This name will appear in chat conversations</small>
              </div>

              <div className="form-group" style={{ marginBottom: 0 }} ref={tzContainerRef}>
                <label>Timezone</label>
                <button
                  ref={tzButtonRef}
                  type="button"
                  onClick={() => {
                    if (!tzOpen && tzButtonRef.current) {
                      const rect = tzButtonRef.current.getBoundingClientRect();
                      setTzDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
                    }
                    setTzOpen(!tzOpen);
                    setTzSearch('');
                  }}
                  style={{
                    width: '100%', padding: '9px 12px', border: '1px solid var(--color-border-soft, #ECECEF)',
                    borderRadius: 8, fontSize: 14, background: 'var(--color-surface, #fff)',
                    color: 'var(--color-text, #1a1a1a)', textAlign: 'left', cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}
                >
                  <span>{timezone.replace(/_/g, ' ')}</span>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" style={{ opacity: 0.5, flexShrink: 0 }}>
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 8l4 4 4-4" />
                  </svg>
                </button>
                {tzOpen && createPortal(
                  <div
                    ref={tzDropdownRef}
                    style={{
                      position: 'fixed', top: tzDropdownPos.top, left: tzDropdownPos.left, width: tzDropdownPos.width,
                      background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #DADCE0)',
                      borderRadius: 8, boxShadow: 'var(--popover-shadow, 0 4px 16px rgba(0,0,0,0.12))', zIndex: 20000,
                      maxHeight: 240, display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    }}
                  >
                    <input
                      type="text"
                      value={tzSearch}
                      onChange={(e) => setTzSearch(e.target.value)}
                      placeholder="Search timezones..."
                      autoFocus
                      style={{
                        padding: '8px 12px', border: 'none', borderBottom: '1px solid var(--color-border-soft, #ECECEF)',
                        fontSize: 13, outline: 'none', background: 'transparent', color: 'var(--color-text)',
                      }}
                    />
                    <div className="xv-scroll" style={{ overflowY: 'auto', flex: 1 }}>
                      {Intl.supportedValuesOf('timeZone')
                        .filter(tz => tz.toLowerCase().includes(tzSearch.toLowerCase()))
                        .map(tz => (
                          <button
                            key={tz}
                            type="button"
                            onClick={() => {
                              setTimezone(tz);
                              saveProfile({ timezone: tz });
                              setTzOpen(false);
                              setTzSearch('');
                            }}
                            style={{
                              display: 'block', width: '100%', padding: '7px 12px', border: 'none',
                              background: tz === timezone ? 'var(--color-surface-hover, #F1F3F4)' : 'transparent',
                              color: 'var(--color-text)', fontSize: 13, textAlign: 'left', cursor: 'pointer',
                              fontWeight: tz === timezone ? 500 : 400,
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-surface-hover, #F1F3F4)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = tz === timezone ? 'var(--color-surface-hover, #F1F3F4)' : 'transparent'}
                          >
                            {tz.replace(/_/g, ' ')}
                          </button>
                        ))
                      }
                    </div>
                  </div>,
                  document.body
                )}
                <small className="field-hint">Used for briefing greetings and scheduling</small>
              </div>

              <div className="form-group" style={{ marginBottom: 0, marginTop: 16 }}>
                <label>Email</label>
                <div className="readonly-field">{profile?.email || 'Not available'}</div>
              </div>
            </div>
          </div>

          {/* Billing — only for paid owners. Members of someone else's
              workspace shouldn't see manage-billing controls. */}
          {isPaidOwner && (
            <div className="profile-section" style={{ marginTop: 16 }}>
              <div style={{ background: 'var(--color-surface-tinted, #F8F9FA)', border: '1px solid var(--color-border-soft, #ECECEF)', borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)', marginBottom: 8 }}>Billing</div>
                <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: '0 0 12px', lineHeight: 1.4 }}>
                  Manage your subscription, update payment method, change plans, view invoices, or cancel.
                </p>
                <button
                  type="button"
                  onClick={async () => {
                    // Native iOS/Android Capacitor shell only — opening Stripe inside the webview traps the user.
                    if (Capacitor.isNativePlatform()) {
                      setPortalError('Please manage your subscription on the web. Mobile billing portal is coming soon.');
                      return;
                    }
                    if (!token) return;
                    setOpeningPortal(true);
                    setPortalError(null);
                    try {
                      const res = await apiFetch<{ url?: string }>('payment/portal', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ returnUrl: window.location.href }),
                        authToken: token,
                      });
                      if (res?.url) {
                        window.location.href = res.url;
                        return;
                      }
                      setPortalError('Could not open billing portal. Please try again.');
                    } catch (err) {
                      const message = err instanceof Error ? err.message : 'Failed to open billing portal.';
                      setPortalError(message);
                    } finally {
                      setOpeningPortal(false);
                    }
                  }}
                  disabled={openingPortal}
                  style={{
                    padding: '8px 16px', borderRadius: 8, border: 'none',
                    background: '#0EA5E9', color: 'white', fontSize: 13, fontWeight: 500,
                    cursor: openingPortal ? 'not-allowed' : 'pointer', opacity: openingPortal ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => { if (!openingPortal) e.currentTarget.style.background = '#0284C7'; }}
                  onMouseLeave={(e) => { if (!openingPortal) e.currentTarget.style.background = '#0EA5E9'; }}
                >
                  {openingPortal ? 'Opening…' : 'Manage billing'}
                </button>
                {portalError && (
                  <p style={{ marginTop: 8, fontSize: 12, color: 'var(--color-danger, #EA4335)' }}>{portalError}</p>
                )}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
